import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import AESTest from './AESTest';
import NFTDisplay from './NFTDisplay';
import AESService from './services/aesService';
import AdminLogin from './components/AdminLogin';
import UserManagement from './components/UserManagement';
import UserLogin from './components/UserLogin';
import UserRegistration from './components/UserRegistration';
import LandingPage from './components/LandingPage';
import EncryptedDataViewer from './components/EncryptedDataViewer';

function App() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fabricStatus, setFabricStatus] = useState('checking');
  
  // Form state
  const [formData, setFormData] = useState({
    deviceId: '',
    type: 'sensor',
    manufacturer: '',
    registeredBy: '',
    publicKey: '',
    serialNumber: '',
    hardwareId: '',
    macAddress: '',
    model: ''
  });

  const [updateFormData, setUpdateFormData] = useState({
    deviceType: '',
    manufacturer: '',
    publicKey: ''
  });

  const [selectedDevice, setSelectedDevice] = useState(null);

  // Admin state
  const [adminUser, setAdminUser] = useState(null);
  const [currentView, setCurrentView] = useState('auth'); // 'auth', 'devices', 'admin-login', 'user-management', 'encrypted-data'
  
  // User state
  const [user, setUser] = useState(null);
  const [authView, setAuthView] = useState('login'); // 'login', 'register'

  // API base URL
  const API_BASE = 'http://localhost:5000/api';

  // Check Fabric status and sessions
  useEffect(() => {
    checkFabricStatus();
    loadDevices();
    checkAdminSession();
    checkUserSession();
  }, []);

  const checkAdminSession = () => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    
    if (token && user) {
      try {
        const adminUser = JSON.parse(user);
        setAdminUser(adminUser);
        setCurrentView('devices');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to parse admin user:', error);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    }
  };

  const handleAdminLogin = (user) => {
    setAdminUser(user);
    setCurrentView('devices');
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
    setCurrentView('auth');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    delete axios.defaults.headers.common['Authorization'];
  };

  const checkUserSession = () => {
    const token = localStorage.getItem('userToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setUser(user);
        setCurrentView('devices');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
      }
    }
  };

  const handleUserLogin = (userData) => {
    setUser(userData);
    setCurrentView('devices');
  };

  const handleUserRegistration = (userData) => {
    setUser(userData);
    setCurrentView('devices');
  };

  const handleUserLogout = () => {
    setUser(null);
    setCurrentView('auth');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    delete axios.defaults.headers.common['Authorization'];
  };

  const switchToUserLogin = () => {
    setAuthView('login');
  };

  const switchToUserRegistration = () => {
    setAuthView('register');
  };

  const checkFabricStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/devices`);
      // If we can get devices, Fabric is likely working
      setFabricStatus('fabric');
    } catch (err) {
      setFabricStatus('mock');
    }
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/devices`);
      // Automatically decrypt sensitive data for display
      const decryptedDevices = await Promise.all(
        response.data.map(async (device) => {
          try {
            return await AESService.decryptDeviceData(device);
          } catch (error) {
            console.warn('Failed to decrypt device data:', error);
            return device; // Return original data if decryption fails
          }
        })
      );
      setDevices(decryptedDevices);
      setError('');
    } catch (err) {
      setError('Failed to load devices: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateInputChange = (e) => {
    setUpdateFormData({
      ...updateFormData,
      [e.target.name]: e.target.value
    });
  };

  const registerDevice = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Automatically encrypt sensitive data before sending
      const encryptedData = await AESService.encryptDeviceData(formData);
      const response = await axios.post(`${API_BASE}/devices/register`, encryptedData);
      setMessage('Device registered successfully with automatic encryption! 🔐');
      setFormData({ deviceId: '', type: 'sensor', manufacturer: '', registeredBy: '', publicKey: '', serialNumber: '', hardwareId: '', macAddress: '', model: '' });
      loadDevices();
      setError('');
    } catch (err) {
      setError('Failed to register device: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDevice = async (deviceId) => {
    try {
      const response = await axios.get(`${API_BASE}/devices/${deviceId}`);
      // Unwrap if response is wrapped (e.g., { data: { ...device } })
      const deviceObj = response.data.data || response.data;
      // Automatically decrypt sensitive data for display
      const decryptedDevice = await AESService.decryptDeviceData(deviceObj);
      setSelectedDevice(decryptedDevice);
      setError('');
    } catch (err) {
      setError('Failed to get device: ' + err.message);
    }
  };

  const updateDevice = async (deviceId) => {
    setLoading(true);
    try {
      // Automatically encrypt sensitive data before updating
      const encryptedData = await AESService.encryptDeviceData(updateFormData);
      const response = await axios.put(`${API_BASE}/devices/${deviceId}`, encryptedData);
      setMessage('Device updated successfully with automatic encryption! 🔐');
      setUpdateFormData({ deviceType: '', manufacturer: '', publicKey: '' });
      loadDevices();
      setError('');
    } catch (err) {
      setError('Failed to update device: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const activateDevice = async (deviceId) => {
    try {
      await axios.post(`${API_BASE}/devices/${deviceId}/activate`);
      setMessage('Device activated successfully!');
      loadDevices();
      setError('');
    } catch (err) {
      setError('Failed to activate device: ' + err.message);
    }
  };

  const deactivateDevice = async (deviceId) => {
    try {
      await axios.post(`${API_BASE}/devices/${deviceId}/deactivate`);
      setMessage('Device deactivated successfully!');
      loadDevices();
      setError('');
    } catch (err) {
      setError('Failed to deactivate device: ' + err.message);
    }
  };

  const deleteDevice = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        await axios.delete(`${API_BASE}/devices/${deviceId}`);
        setMessage('Device deleted successfully!');
        loadDevices();
        setError('');
      } catch (err) {
        setError('Failed to delete device: ' + err.message);
      }
    }
  };

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  // Render landing page as default
  if (currentView === 'auth') {
    return (
      <LandingPage 
        onUserLogin={() => setCurrentView('user-auth')}
        onAdminLogin={() => setCurrentView('admin-login')}
      />
    );
  }

  // Render user authentication views
  if (currentView === 'user-auth') {
    if (authView === 'login') {
      return <UserLogin onLoginSuccess={handleUserLogin} onSwitchToRegistration={switchToUserRegistration} />;
    } else {
      return <UserRegistration onRegistrationSuccess={handleUserRegistration} onSwitchToLogin={switchToUserLogin} />;
    }
  }

  // Render admin login if not logged in and trying to access admin features
  if (currentView === 'admin-login') {
    return <AdminLogin onLoginSuccess={handleAdminLogin} />;
  }

  // Render user management if admin is logged in and viewing user management
  if (currentView === 'user-management' && adminUser) {
    return (
      <div className="App">
        <div className="header">
          <h1>🔗 IoT Device Registry</h1>
          <p>Hyperledger Fabric Blockchain Management</p>
          <div className="admin-nav">
            <button onClick={() => setCurrentView('devices')} className="nav-btn">
              📱 Devices
            </button>
            <button onClick={() => setCurrentView('user-management')} className="nav-btn active">
              👥 Users
            </button>
            <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn">
              🔐 Encrypted Data
            </button>
            <button onClick={handleAdminLogout} className="nav-btn logout">
              🚪 Logout ({adminUser.username})
            </button>
          </div>
        </div>
        <UserManagement />
      </div>
    );
  }

  // Render encrypted data viewer
  if (currentView === 'encrypted-data' && (adminUser || user)) {
    return (
      <div className="App">
        <div className="header">
          <h1>🔗 IoT Device Registry</h1>
          <p>Hyperledger Fabric Blockchain Management</p>
          <div className="admin-nav">
            <button onClick={() => setCurrentView('devices')} className="nav-btn">
              📱 Devices
            </button>
            {adminUser && (
              <button onClick={() => setCurrentView('user-management')} className="nav-btn">
                👥 Users
              </button>
            )}
            <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn active">
              🔐 Encrypted Data
            </button>
            <button onClick={adminUser ? handleAdminLogout : handleUserLogout} className="nav-btn logout">
              🚪 Logout ({adminUser ? adminUser.username : user.username})
            </button>
          </div>
        </div>
        <EncryptedDataViewer />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <h1>🔗 IoT Device Registry</h1>
        <p>Hyperledger Fabric Blockchain Management</p>
        <div className="admin-nav">
          {adminUser ? (
            <>
              <button onClick={() => setCurrentView('devices')} className="nav-btn active">
                📱 Devices
              </button>
              <button onClick={() => setCurrentView('user-management')} className="nav-btn">
                👥 Users
              </button>
              <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn">
                🔐 Encrypted Data
              </button>
              <button onClick={handleAdminLogout} className="nav-btn logout">
                🚪 Logout ({adminUser.username})
              </button>
            </>
          ) : user ? (
            <>
              <button onClick={() => setCurrentView('devices')} className="nav-btn active">
                📱 Devices
              </button>
              <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn">
                🔐 Encrypted Data
              </button>
              <button onClick={handleUserLogout} className="nav-btn logout">
                🚪 Logout ({user.username})
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setCurrentView('auth')} className="nav-btn home">
                🏠 Home
              </button>
            </>
          )}
        </div>
      </div>

      <div className="container">
        {/* Status Indicator */}
        {fabricStatus === 'fabric' && (
          <div className="fabric-status">
            ✅ Connected to Hyperledger Fabric Network
          </div>
        )}
        {fabricStatus === 'mock' && (
          <div className="mock-status">
            ⚠️ Running in Mock Mode (Fabric connection failed)
          </div>
        )}
        
        {/* AES Encryption Status */}
        <div style={{ 
          backgroundColor: '#d1ecf1', 
          border: '1px solid #bee5eb', 
          borderRadius: '4px', 
          padding: '10px', 
          marginBottom: '20px',
          color: '#0c5460'
        }}>
          🔐 <strong>Automatic AES Encryption Enabled</strong>
          <br />
          <small>Sensitive fields (Public Key, Serial Number, Hardware ID, MAC Address) are automatically encrypted before storage and decrypted for display.</small>
        </div>

        {/* Messages */}
        {message && (
          <div className="alert alert-success">
            {message}
            <button onClick={clearMessages} style={{ float: 'right', background: 'none', border: 'none', color: '#155724' }}>×</button>
          </div>
        )}
        {error && (
          <div className="alert alert-error">
            {error}
            <button onClick={clearMessages} style={{ float: 'right', background: 'none', border: 'none', color: '#721c24' }}>×</button>
          </div>
        )}

        {/* Register Device Form */}
        <div className="card">
          <h2>📝 Register New Device</h2>
          <form onSubmit={registerDevice}>
            <div className="form-group">
              <label>Device ID:</label>
              <input
                type="text"
                name="deviceId"
                value={formData.deviceId}
                onChange={handleInputChange}
                required
                placeholder="e.g., device-001"
              />
            </div>
            <div className="form-group">
              <label>Device Type:</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="sensor">Sensor</option>
                <option value="actuator">Actuator</option>
                <option value="gateway">Gateway</option>
                <option value="controller">Controller</option>
              </select>
            </div>
            <div className="form-group">
              <label>Manufacturer:</label>
              <input
                type="text"
                name="manufacturer"
                value={formData.manufacturer}
                onChange={handleInputChange}
                required
                placeholder="e.g., TechCorp"
              />
            </div>
            <div className="form-group">
              <label>Registered By:</label>
              <input
                type="text"
                name="registeredBy"
                value={formData.registeredBy}
                onChange={handleInputChange}
                required
                placeholder="e.g., John Doe"
              />
            </div>
            <div className="form-group">
              <label>🔐 Public Key (Auto-Encrypted):</label>
              <input
                type="text"
                name="publicKey"
                value={formData.publicKey}
                onChange={handleInputChange}
                placeholder="e.g., public-key-123"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>This field will be automatically encrypted</small>
            </div>
            <div className="form-group">
              <label>🔐 Serial Number (Auto-Encrypted):</label>
              <input
                type="text"
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleInputChange}
                placeholder="e.g., SN123456789"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>This field will be automatically encrypted</small>
            </div>
            <div className="form-group">
              <label>🔐 Hardware ID (Auto-Encrypted):</label>
              <input
                type="text"
                name="hardwareId"
                value={formData.hardwareId}
                onChange={handleInputChange}
                placeholder="e.g., HW-ABC-123"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>This field will be automatically encrypted</small>
            </div>
            <div className="form-group">
              <label>🔐 MAC Address (Auto-Encrypted):</label>
              <input
                type="text"
                name="macAddress"
                value={formData.macAddress}
                onChange={handleInputChange}
                placeholder="e.g., 00:11:22:33:44:55"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>This field will be automatically encrypted</small>
            </div>
            <div className="form-group">
              <label>Model:</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                placeholder="e.g., ECG-2024"
              />
            </div>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Registering...' : 'Register Device'}
            </button>
          </form>
        </div>

        {/* Update Device Form */}
        <div className="card">
          <h2>✏️ Update Device</h2>
          <div className="form-group">
            <label>Device ID to Update:</label>
            <input
              type="text"
              placeholder="Enter device ID"
              onChange={(e) => setSelectedDevice({ deviceId: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>New Device Type:</label>
            <input
              type="text"
              name="deviceType"
              value={updateFormData.deviceType}
              onChange={handleUpdateInputChange}
              placeholder="e.g., humidity-sensor"
            />
          </div>
          <div className="form-group">
            <label>Manufacturer:</label>
            <input
              type="text"
              name="manufacturer"
              value={updateFormData.manufacturer}
              onChange={handleUpdateInputChange}
              placeholder="e.g., TechCorp"
            />
          </div>
          <div className="form-group">
            <label>🔐 Public Key (Auto-Encrypted):</label>
            <input
              type="text"
              name="publicKey"
              value={updateFormData.publicKey}
              onChange={handleUpdateInputChange}
              placeholder="e.g., new-public-key-123"
            />
            <small style={{ color: '#666', fontSize: '12px' }}>This field will be automatically encrypted</small>
          </div>
          <button 
            onClick={() => updateDevice(selectedDevice?.deviceId)} 
            className="btn"
            disabled={!selectedDevice?.deviceId || loading}
          >
            {loading ? 'Updating...' : 'Update Device'}
          </button>
        </div>

        {/* Device List */}
        <div className="card">
          <h2>📱 Registered Devices ({devices.length})</h2>
          <button onClick={loadDevices} className="btn" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh Devices'}
          </button>
          
          {loading ? (
            <div className="loading">Loading devices...</div>
          ) : devices.length === 0 ? (
            <div className="loading">No devices registered yet.</div>
          ) : (
            <div className="device-list">
              {devices.map((device, index) => (
                <div key={index} className="device-card">
                  <h3>{device.deviceId}</h3>
                  <div className="device-info">
                    <strong>Status:</strong> 
                    <span className={device.status === 'active' ? 'status-active' : 'status-inactive'}>
                      {device.status}
                    </span>
                  </div>
                  <div className="device-info">
                    <strong>Timestamp:</strong> {new Date(device.timestamp).toLocaleString()}
                  </div>
                                     {device.type && (
                     <div className="device-info">
                       <strong>Type:</strong> {device.type}
                     </div>
                   )}
                   {device.manufacturer && (
                     <div className="device-info">
                       <strong>Manufacturer:</strong> {device.manufacturer}
                     </div>
                   )}
                   {device.registeredBy && (
                     <div className="device-info">
                       <strong>Registered By:</strong> {device.registeredBy}
                     </div>
                   )}
                  <div style={{ marginTop: '15px' }}>
                    <button 
                      onClick={() => getDevice(device.deviceId)} 
                      className="btn"
                    >
                      View Details
                    </button>
                    <button 
                      onClick={() => activateDevice(device.deviceId)} 
                      className="btn btn-success"
                    >
                      Activate
                    </button>
                    <button 
                      onClick={() => deactivateDevice(device.deviceId)} 
                      className="btn"
                    >
                      Deactivate
                    </button>
                    <button 
                      onClick={() => deleteDevice(device.deviceId)} 
                      className="btn btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Device Details */}
        {selectedDevice && (
          <div className="card">
            <h2>🔍 Device Details</h2>
            <pre style={{ background: '#f8f9fa', padding: '15px', borderRadius: '4px', overflow: 'auto' }}>
              {JSON.stringify(selectedDevice, null, 2)}
            </pre>
            <button onClick={() => setSelectedDevice(null)} className="btn">
              Close Details
            </button>
            {/* NFT Display - always show, even if nftIdentity is missing */}
            <NFTDisplay 
              deviceId={selectedDevice.deviceId} 
              nftIdentity={selectedDevice.nftIdentity || {}} 
              showMissingMessage={!selectedDevice.nftIdentity}
            />
          </div>
        )}
        {/* AES Encryption Test */}
        <AESTest />
      </div>
    </div>
  );
}

export default App; 